import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../services/api';
import LegacyFALogo from './ui/LegacyFALogo';
import * as pdfjsLib from 'pdfjs-dist';
import {
  FileText, CheckCircle, PenTool, Type, X, Save, RotateCcw,
  Minus, Plus, ChevronLeft, ChevronRight, ChevronDown, Smartphone, Check, KeyRound,
} from 'lucide-react';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3000/api/v1';

/* ═══════════════════════════════════════════════
   Main SigningInterface
   ═══════════════════════════════════════════════ */
const SigningInterface = () => {
  const { token } = useParams();
  const [status, setStatus] = useState('loading'); // loading | password | consent | signing | completed | error
  const [errorMessage, setErrorMessage] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [session, setSession] = useState(null);
  const [fields, setFields] = useState([]);
  const [completedFields, setCompletedFields] = useState([]);
  const [filledFields, setFilledFields] = useState({});
  const [activeField, setActiveField] = useState(null);
  const [sessionSignature, setSessionSignature] = useState(null);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [pdfPages, setPdfPages] = useState([]);
  const [scale, setScale] = useState(1.2);
  const [submitting, setSubmitting] = useState(false);
  const [currentFieldIdx, setCurrentFieldIdx] = useState(-1);
  const [showHostAuth, setShowHostAuth] = useState(false);
  const fieldRefs = useRef({});

  // Multi-document state
  const [documents, setDocuments] = useState([]); // [{id, title, token, pages}]
  const [activeDocIndex, setActiveDocIndex] = useState(0);
  const [activeToken, setActiveToken] = useState(token); // token for current doc API calls
  const [showViewDocs, setShowViewDocs] = useState(false);
  const [docFieldsCache, setDocFieldsCache] = useState({}); // {docToken: {fields, completedFields, filledFields}}

  // Password / Access code state
  const [requiresPassword, setRequiresPassword] = useState(false);
  const [signingData, setSigningData] = useState(null);

  // Geolocation
  const [geoLocation, setGeoLocation] = useState(null);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setGeoLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy }),
        () => {},
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
      );
    }
  }, []);

  useEffect(() => { if (token) loadSigningData(); }, [token]); // eslint-disable-line

  const loadSigningData = async () => {
    try {
      const res = await api.getSigningData(token);
      if (res?.success && res.signing_data) {
        if (res.signing_data.requires_password) {
          setSigningData(res.signing_data);
          setRequiresPassword(true);
          setStatus('password');
        } else {
          setSigningData(res.signing_data);
          setStatus('consent'); // show consent dialog first
        }
      } else {
        setStatus('error');
        setErrorMessage('Unable to load signing data');
      }
    } catch (err) {
      setErrorMessage(err?.data?.error || err?.message || 'Signing link not found or expired');
      setErrorCode(err?.data?.code || '');
      setStatus('error');
    }
  };

  const handlePasswordVerified = () => {
    setRequiresPassword(false);
    setStatus('consent');
  };

  const handleConsentAccepted = () => {
    initSigning(signingData);
  };

  const initSigning = (data) => {
    setSession(data);
    const allFields = data.signature_fields || [];
    setFields(allFields);
    setCompletedFields(data.completed_fields || []);
    setStatus('signing');

    // Build documents list from bundle_documents or single document
    // IMPORTANT: Always use the URL `token` as the initial active token since
    // the fields loaded from `data` belong to this token's document/recipient.
    const bundleDocs = data.bundle_documents || data.documents || [];
    let initialDocIndex = 0;
    if (bundleDocs.length > 1) {
      const docsList = bundleDocs.map((d, i) => ({ id: d.id || d.document_id, title: d.title || `Document ${i + 1}`, token: d.signing_token || token, total_pages: d.total_pages }));
      setDocuments(docsList);
      // Find which bundle doc matches the URL token
      const matchIdx = docsList.findIndex(d => d.token === token);
      initialDocIndex = matchIdx >= 0 ? matchIdx : 0;
    } else {
      const doc = data.document || {};
      setDocuments([{ id: doc.id, title: doc.title || 'Document', token, total_pages: doc.total_pages }]);
    }
    setActiveDocIndex(initialDocIndex);
    setActiveToken(token); // always use URL token — it matches the fields we loaded
    // Cache current doc fields under the URL token
    setDocFieldsCache(prev => ({ ...prev, [token]: { fields: allFields, completedFields: data.completed_fields || [], filledFields: {} } }));
    loadPDFForToken(token);

    // Auto-fill date & date_signed fields locally — values are sent to the API on Complete.
    const now = new Date();
    const today = `${String(now.getDate()).padStart(2, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()}`;
    // date_signed shows a live datetime preview — the actual final value is overwritten
    // server-side at completion time to the exact signed_at timestamp.
    const MONTHS_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const formatSignedAt = (d) =>
      `${String(d.getDate()).padStart(2,'0')} ${MONTHS_ABBR[d.getMonth()]} ${d.getFullYear()} · ` +
      `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}:${String(d.getSeconds()).padStart(2,'0')}`;
    const autoDateFilled = {};
    allFields.forEach(field => {
      if (field.field_type === 'date_signed') {
        autoDateFilled[field.field_id || field.id] = formatSignedAt(now); // preview only
      } else if (field.field_type === 'date') {
        autoDateFilled[field.field_id || field.id] = today;
      }
    });
    if (Object.keys(autoDateFilled).length > 0) setFilledFields(autoDateFilled);
  };

  const loadPDFForToken = async (signingToken) => {
    try {
      const pdf = await pdfjsLib.getDocument(`${API_BASE}/signing/${signingToken}/pdf`).promise;
      const pages = [];
      for (let i = 1; i <= pdf.numPages; i++) pages.push(await pdf.getPage(i));
      setPdfPages(pages);
    } catch (err) { console.error('Failed to load PDF:', err); }
  };

  const switchDocument = async (index) => {
    if (index === activeDocIndex || !documents[index]) return;
    const newDoc = documents[index];
    const newToken = newDoc.token;

    // Save current doc state to cache
    setDocFieldsCache(prev => ({
      ...prev,
      [activeToken]: { fields, completedFields, filledFields }
    }));

    setActiveDocIndex(index);
    setActiveToken(newToken);
    setShowViewDocs(false);
    setActiveField(null);
    setCurrentFieldIdx(-1);

    // Check cache first
    if (docFieldsCache[newToken]) {
      const cached = docFieldsCache[newToken];
      setFields(cached.fields);
      setCompletedFields(cached.completedFields);
      setFilledFields(cached.filledFields);
    } else {
      // Fetch signing data for this document's token
      try {
        const res = await api.getSigningData(newToken);
        if (res?.success && res.signing_data) {
          const newFields = res.signing_data.signature_fields || [];
          const newCompleted = res.signing_data.completed_fields || [];
          setFields(newFields);
          setCompletedFields(newCompleted);
          setFilledFields({});
          setDocFieldsCache(prev => ({
            ...prev,
            [newToken]: { fields: newFields, completedFields: newCompleted, filledFields: {} }
          }));
        }
      } catch (err) {
        console.error('Failed to load signing data for doc:', err);
        setFields([]);
        setCompletedFields([]);
        setFilledFields({});
      }
    }

    await loadPDFForToken(newToken);
  };

  // Close View Docs dropdown on outside click
  useEffect(() => { if (!showViewDocs) return; const close = () => setShowViewDocs(false); document.addEventListener('click', close); return () => document.removeEventListener('click', close); }, [showViewDocs]);

  const handleFieldClick = (field) => {
    if (filledFields[field.field_id || field.id]) return;
    if (field.field_type === 'signature') {
      if (sessionSignature) {
        // Signature already created — auto-fill this field immediately
        setFilledFields(prev => ({ ...prev, [field.field_id || field.id]: sessionSignature }));
      } else {
        setActiveField(field);
        setShowSignaturePad(true);
      }
      return;
    }
    setActiveField(field);
  };

  // Store field value locally — sent to API in batch when Complete is clicked
  const handleFieldValue = (field, value) => {
    setFilledFields(prev => ({ ...prev, [field.field_id || field.id]: value }));
    setActiveField(null);
  };

  const handleSignatureSave = async (sigData) => {
    try {
      const payload = { ...sigData };
      if (geoLocation) payload.location = geoLocation;
      await api.saveSignature(activeToken, payload); // signature image must be on server for PDF stamping
      const sigValue = sigData.signature_data;
      setSessionSignature(sigValue);
      setShowSignaturePad(false);
      // Auto-fill the field that opened the pad
      if (activeField && activeField.field_type === 'signature') {
        setFilledFields(prev => ({ ...prev, [activeField.field_id || activeField.id]: sigValue }));
        setActiveField(null);
      }
    } catch { alert('Failed to save signature.'); }
  };

  // Current document completion
  const currentDocAllFilled = fields.length > 0 && fields.every(f => filledFields[f.field_id || f.id]);
  const filledCount = Object.keys(filledFields).length;
  const progress = fields.length > 0 ? (filledCount / fields.length) * 100 : 0;
  const noFields = fields.length === 0;

  // Multi-document: check if a cached doc has all fields filled
  const isDocComplete = (docToken) => {
    if (docToken === activeToken) {
      return fields.length === 0 || fields.every(f => filledFields[f.field_id || f.id]);
    }
    const cached = docFieldsCache[docToken];
    if (!cached) return false; // not loaded yet = not complete
    if (!cached.fields || cached.fields.length === 0) return true; // no fields = complete
    return cached.fields.every(f => (cached.filledFields || {})[f.field_id || f.id]);
  };

  // Are ALL documents in the bundle complete?
  const allDocsComplete = documents.length > 0 && documents.every(d => isDocComplete(d.token));

  // Find the next incomplete document index (after current)
  const getNextIncompleteDocIdx = () => {
    for (let i = 0; i < documents.length; i++) {
      const idx = (activeDocIndex + 1 + i) % documents.length;
      if (idx === activeDocIndex) continue;
      if (!isDocComplete(documents[idx].token)) return idx;
    }
    return -1;
  };

  const getNextUnfilledIdx = (afterIdx = -1) => {
    for (let i = afterIdx + 1; i < fields.length; i++) { if (!filledFields[fields[i].field_id || fields[i].id]) return i; }
    return -1;
  };

  const scrollToField = (idx) => {
    if (idx < 0 || idx >= fields.length) return;
    const field = fields[idx];
    const fid = field.field_id || field.id;
    const el = fieldRefs.current[fid];
    if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); setTimeout(() => handleFieldClick(field), 400); }
    setCurrentFieldIdx(idx);
  };

  const handleBottomAction = () => {
    if (currentDocAllFilled || noFields) {
      if (documents.length > 1 && !allDocsComplete) {
        const nextDocIdx = getNextIncompleteDocIdx();
        if (nextDocIdx >= 0) { switchDocument(nextDocIdx); return; }
      }
      return; // all complete — Complete button handles it
    }
    const nextIdx = getNextUnfilledIdx(currentFieldIdx);
    if (nextIdx >= 0) scrollToField(nextIdx);
  };

  // Core completion — called after host auth passes (or directly for remote signing)
  const doComplete = async () => {
    setSubmitting(true);
    try {
      const locationData = geoLocation || null;
      const flushFields = async (docToken, filled) => {
        for (const [fieldId, value] of Object.entries(filled)) {
          await api.fillField(docToken, fieldId, value);
        }
      };
      if (documents.length > 1) {
        for (const doc of documents) {
          if (!doc.token) continue;
          const docFilled = doc.token === activeToken
            ? filledFields
            : (docFieldsCache[doc.token]?.filledFields || {});
          await flushFields(doc.token, docFilled);
          await api.completeSigning(doc.token, { location: locationData });
        }
      } else {
        await flushFields(activeToken, filledFields);
        await api.completeSigning(activeToken, { location: locationData });
      }
      setStatus('completed');
    } catch (err) { alert('Failed to complete signing: ' + err.message); }
    finally { setSubmitting(false); }
  };

  const handleComplete = async () => {
    const isInPerson = session?.recipient?.signing_method === 'in_person';
    if (isInPerson) {
      // Host must be authenticated before completing in-person signing
      const storedToken = localStorage.getItem('auth_token');
      if (storedToken) {
        try {
          await api.verifyHost(activeToken, storedToken);
          await doComplete();
          return;
        } catch {
          // Token invalid or wrong host — fall through to show auth modal
        }
      }
      setShowHostAuth(true);
      return;
    }
    // Remote signing — no auth check needed
    await doComplete();
  };

  const isCurrentFieldPending = () => {
    if (currentFieldIdx < 0 || currentFieldIdx >= fields.length) return false;
    return !filledFields[fields[currentFieldIdx].field_id || fields[currentFieldIdx].id];
  };
  const canClickNext = () => currentDocAllFilled || noFields || currentFieldIdx === -1 || !isCurrentFieldPending();

  const getButtonLabel = () => {
    if (submitting) return 'Completing...';
    if (documents.length <= 1) {
      if (noFields || currentDocAllFilled) return 'Complete Signing';
      if (currentFieldIdx === -1) return 'Start';
      return 'Next';
    }
    if (allDocsComplete) return 'Complete All Documents';
    if (currentDocAllFilled || noFields) return 'Next Document →';
    if (currentFieldIdx === -1) return 'Start';
    return 'Next';
  };

  const getButtonColor = () => {
    if (documents.length <= 1) {
      return (noFields || currentDocAllFilled) ? 'bg-green-600 hover:bg-green-700' : 'bg-teal-700 hover:bg-teal-800';
    }
    if (allDocsComplete) return 'bg-green-600 hover:bg-green-700';
    if (currentDocAllFilled || noFields) return 'bg-blue-600 hover:bg-blue-700';
    return 'bg-teal-700 hover:bg-teal-800';
  };

  const isCompleteAction = () => {
    if (documents.length <= 1) return noFields || currentDocAllFilled;
    return allDocsComplete;
  };

  // ─── Render by status ───
  if (status === 'loading') return <FullPageLoader text="Loading document..." />;
  if (status === 'error') return <ErrorView message={errorMessage} code={errorCode} />;
  if (status === 'completed') return <CompletedView />;

  if (status === 'password') return (
    <AccessCodePage
      token={token}
      recipientName={signingData?.recipient?.name}
      documentTitle={signingData?.document?.title}
      onVerified={handlePasswordVerified}
    />
  );

  if (status === 'consent') return (
    <StartSignDialog
      recipientName={signingData?.recipient?.name}
      documentTitle={signingData?.document?.title}
      onAccept={handleConsentAccepted}
      onCancel={() => window.close()}
    />
  );

  // ─── Signing view ───
  const getInitials = (name) => { if (!name) return '?'; return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2); };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gray-50">
      {/* ═══ White nav bar — matches SigningSpacePage ═══ */}
      <header className="bg-white border-b border-gray-200 flex-shrink-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center py-3">
          <LegacyFALogo size="sm" />
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-full bg-teal-600 text-white flex items-center justify-center text-sm font-bold">
              {getInitials(session?.recipient?.name)}
            </div>
            <span className="text-sm text-gray-700 hidden md:block">{session?.recipient?.name}</span>
          </div>
        </div>
      </header>

      {/* ═══ Teal info bar — document metadata + progress ═══ */}
      <div className="bg-teal-700 text-white flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center justify-between">
          <div className="hidden lg:flex items-center space-x-8 text-xs">
            {[
              { label: 'Subject', value: (session?.document?.title || '-').toUpperCase() },
              { label: 'Created Date', value: session?.document?.created_at_formatted || '-' },
              { label: 'Expired Date', value: session?.document?.expires_at_formatted || '-' },
              { label: 'Enclosed Documents', value: documents.length },
            ].map((item, i) => (
              <div key={i}><p className="text-teal-300 text-[10px]">{item.label}</p><p className="font-bold">{item.value}</p></div>
            ))}
          </div>
          <div className="flex items-center space-x-3 ml-auto">
            <span className="text-xs text-teal-200">{filledCount}/{fields.length}</span>
            <div className="w-24 bg-teal-900 rounded-full h-1.5">
              <div className="bg-green-400 h-1.5 rounded-full transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
        </div>
      </div>

      {/* ═══ Document navigation bar (when multiple docs) ═══ */}
      {documents.length > 1 && (
        <div className="bg-white border-b border-gray-200 flex-shrink-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between">
            <span className="text-sm text-gray-500">Doc {activeDocIndex + 1} of {documents.length}: <span className="font-medium text-gray-700">{documents[activeDocIndex]?.title}</span></span>
            <div className="flex items-center space-x-2">
              <div className="relative">
                <button onClick={(e) => { e.stopPropagation(); setShowViewDocs(v => !v); }} className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600 bg-white hover:bg-gray-50 flex items-center font-medium">
                  View Documents <ChevronDown className="h-3 w-3 ml-1.5 text-gray-400" />
                </button>
                {showViewDocs && (
                  <div className="absolute right-0 top-full mt-1 w-72 bg-white border border-gray-200 rounded-xl shadow-lg z-40 py-1 max-h-72 overflow-y-auto" onClick={e => e.stopPropagation()}>
                    <div className="px-4 py-2 text-[10px] text-gray-400 font-semibold uppercase tracking-wider border-b border-gray-100">{documents.length} document(s)</div>
                    {documents.map((d, idx) => (
                      <button key={d.id || idx} onClick={() => switchDocument(idx)} className={`w-full text-left px-4 py-2.5 flex items-center space-x-3 transition-colors ${activeDocIndex === idx ? 'bg-teal-50' : 'hover:bg-gray-50'}`}>
                        <div className="w-6 h-6 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center flex-shrink-0 text-[10px] font-bold">{idx + 1}</div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm truncate ${activeDocIndex === idx ? 'text-teal-700 font-semibold' : 'text-gray-700'}`}>{d.title}</p>
                          <p className="text-[11px] text-gray-400">{d.total_pages || '?'} page(s)</p>
                        </div>
                        {activeDocIndex === idx && <Check className="h-4 w-4 text-teal-600 flex-shrink-0" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={() => switchDocument(activeDocIndex - 1)} disabled={activeDocIndex <= 0} className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-500 bg-white hover:bg-gray-50 disabled:opacity-30 flex items-center font-medium"><ChevronLeft className="h-3 w-3 mr-0.5" /> Prev</button>
              <button onClick={() => switchDocument(activeDocIndex + 1)} disabled={activeDocIndex >= documents.length - 1} className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-500 bg-white hover:bg-gray-50 disabled:opacity-30 flex items-center font-medium">Next <ChevronRight className="h-3 w-3 ml-0.5" /></button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ PDF Pages ═══ */}
      <main className="flex-1 overflow-auto py-6">
        <div className="flex flex-col items-center space-y-4">
          {pdfPages.map((page, pageIdx) => (
            <PDFPageWithFields
              key={`${activeDocIndex}-${pageIdx}`}
              page={page}
              pageNumber={pageIdx + 1}
              scale={scale}
              fields={fields.filter(f => f.page_number === pageIdx + 1)}
              completedFields={completedFields.filter(f => f.page_number === pageIdx + 1)}
              filledFields={filledFields}
              activeField={activeField}
              sessionSignature={sessionSignature}
              onFieldClick={handleFieldClick}
              onFieldValue={handleFieldValue}
              onOpenSignaturePad={() => setShowSignaturePad(true)}
              fieldRefs={fieldRefs}
            />
          ))}
        </div>
      </main>

      {/* ═══ Bottom bar ═══ */}
      <div className="bg-white border-t shadow-lg sticky bottom-0 z-30 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
          <p className="text-sm text-gray-500">
            {noFields ? 'No fields required — ready to complete' :
              (documents.length > 1 && allDocsComplete) ? '✅ All documents completed' :
              currentDocAllFilled ? (documents.length > 1 ? `✅ This document done — ${documents.length - documents.filter(d => isDocComplete(d.token)).length} remaining` : '✅ All fields completed') :
              currentFieldIdx === -1 ? `${fields.length} field(s) to complete` :
              `Field ${filledCount + 1} of ${fields.length}`}
          </p>
          <button
            onClick={isCompleteAction() ? handleComplete : handleBottomAction}
            disabled={isCompleteAction() ? submitting : !canClickNext()}
            className={`px-6 py-2.5 text-white rounded-lg disabled:opacity-40 font-medium ${getButtonColor()}`}
          >
            {getButtonLabel()}
          </button>
        </div>
      </div>

      {showSignaturePad && <SignaturePadModal onSave={handleSignatureSave} onClose={() => setShowSignaturePad(false)} />}
      {showHostAuth && (
        <HostAuthModal
          signingToken={activeToken}
          hostEmail={session?.recipient?.host_email}
          onVerified={() => { setShowHostAuth(false); doComplete(); }}
          onCancel={() => setShowHostAuth(false)}
        />
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════
   Access Code Page — OTP-style 4-digit input
   Matches Figma: phone icon, centered card, countdown
   ═══════════════════════════════════════════════ */
const AccessCodePage = ({ token, recipientName, documentTitle, onVerified }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleValidate = async () => {
    if (!password.trim()) { setError('Please enter the access code'); return; }
    setVerifying(true);
    setError('');
    try {
      await api.verifySigningPassword(token, password);
      onVerified();
    } catch (err) {
      setError(err?.data?.error || 'Incorrect access code. Please try again.');
      setPassword('');
      inputRef.current?.focus();
    } finally { setVerifying(false); }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50" style={{ backgroundImage: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(241,245,249,0.9) 100%)' }}>
      {/* Logo */}
      <div className="mb-8">
        <LegacyFALogo size="md" />
      </div>

      {/* Card */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 max-w-lg w-full mx-4">
        {/* Lock icon */}
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 bg-teal-50 rounded-2xl flex items-center justify-center">
            <KeyRound className="h-8 w-8 text-teal-400" />
          </div>
        </div>

        <h2 className="text-xl font-bold text-gray-900 text-center mb-3">Enter Access Code</h2>
        <p className="text-sm text-gray-500 text-center mb-8 leading-relaxed px-4">
          The sender has set an access code for this document. Please enter the code you received to proceed with viewing and signing.
        </p>

        {/* Password input */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Access Code</label>
          <div className="relative">
            <input
              ref={inputRef}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={e => { setPassword(e.target.value); setError(''); }}
              onKeyDown={e => e.key === 'Enter' && handleValidate()}
              placeholder="Enter your access code"
              className={`w-full px-4 py-3 border-2 rounded-xl text-base outline-none transition-colors pr-12 ${
                error ? 'border-red-300 focus:border-red-400 focus:ring-2 focus:ring-red-100' :
                password ? 'border-teal-400 focus:border-teal-500 focus:ring-2 focus:ring-teal-100' :
                'border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-100'
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
            >
              {showPassword ? (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L6.59 6.59m7.532 7.532l3.29 3.29M3 3l18 18" /></svg>
              ) : (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
              )}
            </button>
          </div>
        </div>

        {error && <p className="text-red-500 text-sm text-center mb-4">{error}</p>}

        {/* Validate button */}
        <button
          onClick={handleValidate}
          disabled={verifying || !password.trim()}
          className="w-full py-3.5 bg-teal-500 hover:bg-teal-600 text-white rounded-xl font-medium text-base disabled:opacity-50 transition-colors"
        >
          {verifying ? 'Validating...' : 'Validate'}
        </button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   Start Sign Consent Dialog
   Matches Figma: disclosure checkbox, Cancel/Start Sign
   ═══════════════════════════════════════════════ */
const StartSignDialog = ({ recipientName, documentTitle, onAccept, onCancel }) => {
  const [agreed, setAgreed] = useState(false);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">Start Sign</h2>
          <LegacyFALogo size="xs" />
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          <p className="text-sm text-gray-600 mb-5">
            Please read the <button className="text-gray-900 underline font-medium hover:text-teal-700">Electronic Record and Signature Disclosure</button>.
          </p>

          <label className="flex items-start space-x-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={agreed}
              onChange={e => setAgreed(e.target.checked)}
              className="mt-0.5 w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
            />
            <span className="text-sm text-gray-700">
              I agree to use electronic records and signatures.<span className="text-red-500">*</span>
            </span>
          </label>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end space-x-3">
          <button
            onClick={onCancel}
            className="px-5 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onAccept}
            disabled={!agreed}
            className="px-5 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-40 transition-colors"
          >
            Start Sign
          </button>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   PDF Page with overlaid fields
   ═══════════════════════════════════════════════ */
const PDFPageWithFields = ({ page, pageNumber, scale, fields, completedFields = [], filledFields, activeField, sessionSignature, onFieldClick, onFieldValue, onOpenSignaturePad, fieldRefs }) => {
  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);
  const [dims, setDims] = useState({ width: 0, height: 0 });

  useEffect(() => {
    renderPage();
    return () => { if (renderTaskRef.current) { renderTaskRef.current.cancel(); renderTaskRef.current = null; } };
  }, [page, scale]); // eslint-disable-line

  const renderPage = async () => {
    if (!page || !canvasRef.current) return;
    if (renderTaskRef.current) { renderTaskRef.current.cancel(); renderTaskRef.current = null; }
    try {
      const vp = page.getViewport({ scale });
      const c = canvasRef.current;
      c.width = vp.width; c.height = vp.height;
      setDims({ width: vp.width, height: vp.height });
      const task = page.render({ canvasContext: c.getContext('2d'), viewport: vp });
      renderTaskRef.current = task;
      await task.promise;
      renderTaskRef.current = null;
    } catch (err) { if (err?.name !== 'RenderingCancelledException') console.error(err); }
  };

  // PDF page dimensions — available synchronously from the page object.
  // Using page.view avoids any async timing issues with dims state.
  const pdfW = ((page?.view?.[2] || 596) - (page?.view?.[0] || 0));
  const pdfPageHeightPts = ((page?.view?.[3] || 842) - (page?.view?.[1] || 0));

  // Convert PDF y (bottom-left origin, pts) → CSS top (top-left origin, px)
  const pdfY = (yPts, hPts) => (pdfPageHeightPts - yPts - hPts) * scale;

  // Use page.view-derived dimensions for the container so it has the correct size
  // on the very first render (before the async renderPage/setDims cycle completes).
  // This prevents absolute-positioned fields from overflowing a 0-height container.
  const containerWidth = pdfW * scale;
  const containerHeight = pdfPageHeightPts * scale;

  return (
    <div className="relative bg-white shadow-lg overflow-hidden" style={{ width: containerWidth, height: containerHeight }}>
      <canvas ref={canvasRef} className="block" />
      {completedFields.map((field, i) => {
        const fid = field.field_id || field.id;
        const fh = (field.height || 40) * scale;
        const ftop = pdfY(field.y_position || 0, field.height || 40);
        return (
          <div key={`c-${fid || i}`} className="absolute border border-gray-300 rounded bg-gray-50/60"
            style={{ left: (field.x_position || 0) * scale, top: ftop, width: (field.width || 150) * scale, height: fh, zIndex: 5 }}>
            <FilledFieldDisplay field={field} value={field.value} scale={scale} />
          </div>
        );
      })}
      {fields.map((field, i) => {
        const fid = field.field_id || field.id;
        const filled = filledFields[fid];
        const isActive = activeField && (activeField.field_id || activeField.id) === fid;
        const x = (field.x_position || 0) * scale;
        // PDF y-axis: bottom-left origin. CSS top: top-left origin. Flip using actual PDF page height.
        const y = pdfY(field.y_position || 0, field.height || 40);
        const w = (field.width || 150) * scale;
        const h = (field.height || 63) * scale;

        return (
          <div
            key={fid || i}
            ref={el => { if (fieldRefs) fieldRefs.current[fid] = el; }}
            className={`absolute border-2 rounded transition-all
              ${filled ? 'border-green-400 bg-green-50/80' :
                isActive ? 'border-teal-500 bg-teal-50/90 shadow-lg ring-2 ring-teal-300 cursor-pointer' :
                'border-orange-400 bg-orange-50/70 hover:bg-orange-100/80 animate-pulse cursor-pointer'}`}
            style={{ left: x, top: y, width: w, height: h, zIndex: isActive ? 20 : 10 }}
            onClick={() => !filled && onFieldClick(field)}
          >
            {filled ? <FilledFieldDisplay field={field} value={filled} scale={scale} /> :
              isActive ? <ActiveFieldInput field={field} scale={scale} sessionSignature={sessionSignature} onSubmit={v => onFieldValue(field, v)} onOpenSignaturePad={onOpenSignaturePad} /> :
              <EmptyFieldLabel field={field} scale={scale} />}
          </div>
        );
      })}
    </div>
  );
};

/* ═══════════════════════════════════════════════
   Field components
   ═══════════════════════════════════════════════ */
const FIELD_LABELS = { signature: 'Sign here', date: 'Date', date_signed: 'Date Signed', checkbox: '☐', email: 'Email', textfield: 'Text' };

const EmptyFieldLabel = ({ field, scale }) => (
  <div className="flex items-center justify-center h-full text-orange-600" style={{ fontSize: Math.max(10, 12 * scale) }}>
    {field.field_type === 'signature' && <PenTool className="mr-1" style={{ width: 14 * scale, height: 14 * scale }} />}
    <span className="font-medium truncate">{FIELD_LABELS[field.field_type] || field.field_type}</span>
  </div>
);

const FilledFieldDisplay = ({ field, value, scale }) => {
  if (field.field_type === 'signature' && value?.startsWith?.('data:image')) return (
    <img src={value} alt="Signature" className="w-full h-full" style={{ objectFit: 'contain', objectPosition: 'center' }} />
  );
  if (field.field_type === 'checkbox') return <div className="flex items-center justify-center h-full text-green-700 font-bold" style={{ fontSize: 18 * scale }}>✓</div>;
  // No padding — matches the PDF drawText stamp position (no x-offset in the final PDF)
  return <div className="flex items-center h-full text-gray-800 overflow-hidden" style={{ fontSize: Math.max(10, 12 * scale) }}>{value}</div>;
};

const ActiveFieldInput = ({ field, scale, sessionSignature, onSubmit, onOpenSignaturePad }) => {
  const [textVal, setTextVal] = useState('');
  const [dateVal, setDateVal] = useState('');
  const [emailVal, setEmailVal] = useState('');
  const [emailError, setEmailError] = useState('');

  // Signature field: only shown when pad was closed without saving (edge case)
  if (field.field_type === 'signature') {
    return (
      <div className="flex items-center justify-center h-full">
        <button onClick={onOpenSignaturePad} className="px-2 py-1 bg-teal-600 text-white rounded text-xs flex items-center">
          <PenTool className="h-3 w-3 mr-1" /> Sign here
        </button>
      </div>
    );
  }
  if (field.field_type === 'date') {
    return (
      <div className="flex items-center h-full px-1">
        <input type="date" value={dateVal} onChange={e => setDateVal(e.target.value)} autoFocus
          className="w-full border rounded px-1 outline-none" style={{ fontSize: Math.max(10, 11 * scale) }}
          onBlur={() => dateVal && onSubmit(dateVal)}
          onKeyDown={e => e.key === 'Enter' && dateVal && onSubmit(dateVal)} />
      </div>
    );
  }
  if (field.field_type === 'checkbox') {
    return (
      <div className="flex items-center justify-center h-full">
        <button onClick={() => onSubmit('true')} className="px-2 py-1 bg-teal-600 text-white rounded text-xs">✓ Check</button>
      </div>
    );
  }
  if (field.field_type === 'email') {
    const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal);
    const submit = () => { if (!emailVal.trim()) return; if (!isValid) { setEmailError('Please enter a valid email'); return; } setEmailError(''); onSubmit(emailVal); };
    return (
      <div className="flex flex-col justify-center h-full px-1">
        <input type="email" value={emailVal} onChange={e => { setEmailVal(e.target.value); setEmailError(''); }} autoFocus
          placeholder="name@example.com"
          className={`w-full border rounded px-1 outline-none ${emailError ? 'border-red-400' : ''}`}
          style={{ fontSize: Math.max(10, 11 * scale) }}
          onKeyDown={e => e.key === 'Enter' && submit()}
          onBlur={submit} />
        {emailError && <span className="text-red-500 text-xs mt-0.5">{emailError}</span>}
      </div>
    );
  }
  // Default: text field
  return (
    <div className="flex items-center h-full px-1">
      <input type="text" value={textVal} onChange={e => setTextVal(e.target.value)} autoFocus
        placeholder={field.placeholder || 'Type here'}
        className="w-full border rounded px-1 outline-none" style={{ fontSize: Math.max(10, 11 * scale) }}
        onKeyDown={e => e.key === 'Enter' && textVal.trim() && onSubmit(textVal)}
        onBlur={() => textVal.trim() && onSubmit(textVal)} />
    </div>
  );
};

/* ═══════════════════════════════════════════════
   Sub-components
   ═══════════════════════════════════════════════ */
const FullPageLoader = ({ text }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600 mx-auto mb-4" />
      <p className="text-gray-500">{text}</p>
    </div>
  </div>
);

const ErrorView = ({ message, code }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="bg-red-50 border border-red-200 text-red-700 px-8 py-6 rounded-xl max-w-md text-center">
      <div className="text-3xl mb-3">{code === 'CANCELLED' ? '🚫' : code === 'EXPIRED' ? '⏰' : code === 'COMPLETED' ? '✅' : '⚠️'}</div>
      <p className="font-medium text-lg mb-1">{message || 'This signing link is invalid or has expired.'}</p>
      {code && <p className="text-xs text-red-400 mt-2">Code: {code}</p>}
    </div>
  </div>
);

const CompletedView = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Document Signed!</h2>
      <p className="text-gray-600 mb-6">Your signature has been applied successfully.</p>
      <button onClick={() => window.close()} className="px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700">Close Window</button>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════
   Signature canvas crop helper
   Scans all pixels, finds the bounding box of
   non-transparent strokes, adds `padding` px on
   each side, and returns a tight base64 PNG.
   If the canvas is blank, returns the full image.
   ═══════════════════════════════════════════════ */
const cropSignatureCanvas = (canvas, padding = 10) => {
  const ctx = canvas.getContext('2d');
  const { width, height } = canvas;
  const { data } = ctx.getImageData(0, 0, width, height);

  let minX = width, maxX = 0, minY = height, maxY = 0;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const alpha = data[(y * width + x) * 4 + 3];
      if (alpha > 0) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }

  // Blank canvas — nothing drawn
  if (minX > maxX || minY > maxY) return canvas.toDataURL('image/png');

  const cropX = Math.max(0, minX - padding);
  const cropY = Math.max(0, minY - padding);
  const cropW = Math.min(width,  maxX + padding + 1) - cropX;
  const cropH = Math.min(height, maxY + padding + 1) - cropY;

  const tmp = document.createElement('canvas');
  tmp.width  = cropW;
  tmp.height = cropH;
  tmp.getContext('2d').drawImage(canvas, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
  return tmp.toDataURL('image/png');
};

/* ═══════════════════════════════════════════════
   Signature Pad Modal
   ═══════════════════════════════════════════════ */
const SignaturePadModal = ({ onSave, onClose }) => {
  const canvasRef = useRef(null);
  const fullscreenCanvasRef = useRef(null);
  const containerRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [mode, setMode] = useState('draw');
  const [typed, setTyped] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const c = canvasRef.current;
    if (c) { const ctx = c.getContext('2d'); ctx.strokeStyle = '#000'; ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; }
  }, []);

  useEffect(() => {
    if (isFullscreen && fullscreenCanvasRef.current) {
      const ctx = fullscreenCanvasRef.current.getContext('2d');
      ctx.strokeStyle = '#000'; ctx.lineWidth = 3; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    }
  }, [isFullscreen, canvasSize]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    if (isFullscreen) { document.body.style.position = 'fixed'; document.body.style.width = '100%'; document.body.style.height = '100%'; }
    else { document.body.style.position = ''; document.body.style.width = ''; document.body.style.height = ''; }
    return () => { document.body.style.overflow = ''; document.body.style.position = ''; document.body.style.width = ''; document.body.style.height = ''; };
  }, [isFullscreen]);

  useEffect(() => {
    if (!isFullscreen) return;
    const updateSize = () => { if (containerRef.current) { const r = containerRef.current.getBoundingClientRect(); setCanvasSize({ width: r.width - 32, height: r.height - 16 }); } };
    updateSize();
    window.addEventListener('resize', updateSize);
    const t1 = setTimeout(updateSize, 100); const t2 = setTimeout(updateSize, 500);
    return () => { window.removeEventListener('resize', updateSize); clearTimeout(t1); clearTimeout(t2); };
  }, [isFullscreen]);

  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    const p = e => e.preventDefault();
    c.addEventListener('touchstart', p, { passive: false }); c.addEventListener('touchmove', p, { passive: false });
    return () => { c.removeEventListener('touchstart', p); c.removeEventListener('touchmove', p); };
  }, []);

  useEffect(() => {
    const c = fullscreenCanvasRef.current; if (!c) return;
    const p = e => e.preventDefault();
    c.addEventListener('touchstart', p, { passive: false }); c.addEventListener('touchmove', p, { passive: false });
    return () => { c.removeEventListener('touchstart', p); c.removeEventListener('touchmove', p); };
  }, [isFullscreen, canvasSize]);

  const getPos = (e, canvas) => {
    const c = canvas || (isFullscreen ? fullscreenCanvasRef.current : canvasRef.current);
    const r = c.getBoundingClientRect();
    const sx = c.width / r.width; const sy = c.height / r.height;
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (cx - r.left) * sx, y: (cy - r.top) * sy };
  };

  const start = e => { setDrawing(true); setHasDrawn(true); const c = isFullscreen ? fullscreenCanvasRef.current : canvasRef.current; const { x, y } = getPos(e, c); c.getContext('2d').beginPath(); c.getContext('2d').moveTo(x, y); };
  const draw = e => { if (!drawing) return; const c = isFullscreen ? fullscreenCanvasRef.current : canvasRef.current; const { x, y } = getPos(e, c); const ctx = c.getContext('2d'); ctx.lineTo(x, y); ctx.stroke(); };
  const stop = () => setDrawing(false);
  const clear = () => { const c = isFullscreen ? fullscreenCanvasRef.current : canvasRef.current; if (c) c.getContext('2d').clearRect(0, 0, c.width, c.height); setHasDrawn(false); setSignaturePreview(null); };

  const openFullscreen = () => setIsFullscreen(true);
  const closeFullscreen = (saveIt = false) => {
    if (saveIt && fullscreenCanvasRef.current) {
      // Crop the fullscreen canvas to the bounding box of drawn strokes + 10px padding
      const preview = cropSignatureCanvas(fullscreenCanvasRef.current, 10);
      setSignaturePreview(preview);
      if (canvasRef.current) { const ctx = canvasRef.current.getContext('2d'); ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height); const img = new Image(); img.onload = () => { if (canvasRef.current) ctx.drawImage(img, 0, 0, canvasRef.current.width, canvasRef.current.height); }; img.src = preview; }
      setHasDrawn(true);
    }
    setIsFullscreen(false);
  };

  const save = () => {
    if (mode === 'draw') { const data = signaturePreview || cropSignatureCanvas(canvasRef.current, 10); onSave({ signature_data: data, signature_type: 'drawn' }); }
    else if (typed.trim()) onSave({ signature_data: typed.trim(), signature_type: 'typed' });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 max-w-xl w-full">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Create Your Signature</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-400" /></button>
        </div>
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg mb-4">
          {['draw', 'type'].map(m => (
            <button key={m} onClick={() => setMode(m)} className={`flex-1 py-2 rounded-md text-sm font-medium ${mode === m ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`}>
              {m === 'draw' ? <><PenTool className="h-4 w-4 inline mr-1" />Draw</> : <><Type className="h-4 w-4 inline mr-1" />Type</>}
            </button>
          ))}
        </div>
        {mode === 'draw' ? (
          signaturePreview ? (
            <div className="space-y-3">
              <div className="border-2 border-green-300 rounded-lg p-3 bg-green-50">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2"><CheckCircle className="h-4 w-4 text-green-600" /><span className="text-sm font-medium text-green-800">Signature Preview</span></div>
                  <button onClick={() => { setSignaturePreview(null); setHasDrawn(false); openFullscreen(); }} className="text-sm text-teal-600 hover:text-teal-800 flex items-center space-x-1"><RotateCcw className="h-3 w-3" /><span>Re-draw</span></button>
                </div>
                <div className="bg-white rounded-lg border p-2"><img src={signaturePreview} alt="Signature" className="w-full h-auto max-h-36 object-contain" /></div>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 cursor-pointer hover:border-teal-400 hover:bg-teal-50/30 transition-colors" onClick={openFullscreen}>
                <canvas ref={canvasRef} width={460} height={180} className="w-full border rounded-lg cursor-pointer bg-white pointer-events-none" />
                <div className="flex items-center justify-center mt-2 text-gray-500"><PenTool className="h-3 w-3 mr-1" /><span className="text-xs">Tap to open full-screen drawing pad</span></div>
              </div>
              <button onClick={clear} className="text-sm text-gray-500">Clear</button>
            </div>
          )
        ) : (
          <div className="space-y-3">
            <input type="text" value={typed} onChange={e => setTyped(e.target.value)} placeholder="Type your full name" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none" />
            {typed && <div className="border rounded-lg p-4 bg-gray-50 text-center text-2xl font-serif">{typed}</div>}
          </div>
        )}
        <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700">Cancel</button>
          <button onClick={save} disabled={mode === 'draw' ? (!hasDrawn && !signaturePreview) : !typed.trim()} className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-40 flex items-center">
            <Save className="h-4 w-4 mr-1" /> Save
          </button>
        </div>
      </div>

      {isFullscreen && (
        <div className="fixed inset-0 z-[200] bg-white flex flex-col" style={{ touchAction: 'none' }}>
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b shrink-0">
            <div className="flex items-center space-x-2"><PenTool className="h-5 w-5 text-teal-600" /><span className="font-semibold text-gray-900">Draw Your Signature</span></div>
            <button onClick={() => closeFullscreen(false)} className="p-2 hover:bg-gray-200 rounded-full"><X className="h-5 w-5 text-gray-600" /></button>
          </div>
          <div ref={containerRef} className="flex-1 flex items-center justify-center p-4 bg-gray-100 overflow-hidden">
            {canvasSize.width > 0 && (
              <canvas ref={fullscreenCanvasRef} width={canvasSize.width * 2} height={canvasSize.height * 2}
                className="bg-white rounded-xl shadow-lg cursor-crosshair touch-none" style={{ width: canvasSize.width, height: canvasSize.height, touchAction: 'none' }}
                onMouseDown={start} onMouseMove={draw} onMouseUp={stop} onMouseLeave={stop} onTouchStart={start} onTouchMove={draw} onTouchEnd={stop} />
            )}
          </div>
          <div className="flex items-center justify-between px-4 py-3 bg-white border-t shrink-0">
            <button onClick={clear} className="flex items-center space-x-2 px-4 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"><RotateCcw className="h-4 w-4" /><span className="text-sm font-medium">Clear</span></button>
            <div className="flex items-center space-x-3">
              <button onClick={() => closeFullscreen(false)} className="px-4 py-2.5 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium">Cancel</button>
              <button onClick={() => closeFullscreen(true)} className="flex items-center space-x-2 px-6 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm font-medium shadow-sm"><CheckCircle className="h-4 w-4" /><span>Done</span></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════
   Host Auth Modal — in-person signing gate
   Shown when signing_method === 'in_person' and
   the current auth token is missing / invalid.
   ═══════════════════════════════════════════════ */
const HostAuthModal = ({ signingToken, hostEmail, onVerified, onCancel }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const emailRef = useRef(null);

  useEffect(() => { emailRef.current?.focus(); }, []);

  const handleLogin = async () => {
    if (!email.trim() || !password.trim()) { setError('Email and password are required'); return; }
    setLoading(true);
    setError('');
    try {
      // 1. Login to get a fresh JWT
      const loginRes = await api.login(email.trim(), password);
      if (!loginRes.success) { setError('Invalid email or password'); return; }
      const newToken = loginRes.token;

      // 2. Verify this user is the designated host for this signing session
      await api.verifyHost(signingToken, newToken);

      // 3. Persist the token for future use
      localStorage.setItem('auth_token', newToken);
      api.setToken(newToken);

      onVerified();
    } catch (err) {
      const msg = err?.data?.error || err?.message || 'Authentication failed';
      // Friendly message when user is authenticated but wrong host
      if (err?.status === 403) {
        setError(`Wrong account. This session requires the designated host to authenticate.`);
      } else {
        setError(msg);
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-teal-50">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 rounded-xl bg-teal-100 flex items-center justify-center">
              <KeyRound className="h-5 w-5 text-teal-600" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">Host Authentication Required</h3>
              <p className="text-xs text-teal-600 font-medium">In-Person Signing</p>
            </div>
          </div>
          <button onClick={onCancel} className="p-1.5 hover:bg-teal-100 rounded-lg transition-colors">
            <X className="h-4 w-4 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <p className="text-sm text-gray-500 mb-5 leading-relaxed">
            This is an <span className="font-semibold text-gray-700">in-person signing session</span>. The designated host must verify their identity before the document can be completed.
            {hostEmail && <><br /><span className="text-xs text-gray-400 mt-1 block">Expected host: <span className="font-medium text-gray-600">{hostEmail}</span></span></>}
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
              <input
                ref={emailRef}
                type="email"
                value={email}
                onChange={e => { setEmail(e.target.value); setError(''); }}
                onKeyDown={e => e.key === 'Enter' && handleLogin()}
                placeholder="host@example.com"
                className={`w-full px-4 py-2.5 border-2 rounded-xl text-sm outline-none transition-colors ${error ? 'border-red-300 focus:border-red-400' : 'border-gray-200 focus:border-teal-500'}`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPwd ? 'text' : 'password'}
                  value={password}
                  onChange={e => { setPassword(e.target.value); setError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleLogin()}
                  placeholder="Your password"
                  className={`w-full px-4 py-2.5 border-2 rounded-xl text-sm outline-none transition-colors pr-11 ${error ? 'border-red-300 focus:border-red-400' : 'border-gray-200 focus:border-teal-500'}`}
                />
                <button type="button" onClick={() => setShowPwd(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPwd
                    ? <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L6.59 6.59m7.532 7.532l3.29 3.29M3 3l18 18" /></svg>
                    : <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                  }
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="mt-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end space-x-3">
          <button onClick={onCancel} className="px-5 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors">
            Cancel
          </button>
          <button
            onClick={handleLogin}
            disabled={loading || !email.trim() || !password.trim()}
            className="px-5 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-medium disabled:opacity-40 transition-colors"
          >
            {loading ? 'Verifying...' : 'Verify & Complete'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SigningInterface;
